import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Facebook, Instagram, Linkedin, ShoppingBag, ArrowRightLeft, Puzzle, Code, Palette, Globe, Cpu, Bot, Wrench, Rocket } from "lucide-react";
import { useMenuItems, type MenuItem } from "@/hooks/useMenuItems";
import { useLanguage } from "@/contexts/LanguageContext";
import wavaiLogo from "@/assets/logo-wavai.svg";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { supabase } from "@/integrations/supabase/client";
import { resolveAssetUrl } from "@/lib/media";

import shopifyLogo from "@/assets/partners/shopify.svg";
import wordpressLogo from "@/assets/partners/wordpress.svg";
import woocommerceLogo from "@/assets/partners/woocommerce.svg";
import webflowLogo from "@/assets/partners/webflow.svg";
import lovableLogo from "@/assets/partners/lovable.svg";

const BRAND_MAP: Record<string, { logo: string; bg: string }> = {
  "/shopify":      { logo: shopifyLogo,     bg: "rgba(149, 191, 71, 0.12)" },
  "/wordpress":    { logo: wordpressLogo,   bg: "rgba(33, 117, 155, 0.12)" },
  "/woocommerce":  { logo: woocommerceLogo, bg: "rgba(126, 92, 168, 0.12)" },
  "/webflow":      { logo: webflowLogo,     bg: "rgba(67, 83, 255, 0.12)" },
  "/lovable":      { logo: lovableLogo,     bg: "rgba(255, 75, 85, 0.12)" },
};

const DROPDOWN_STYLE: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  "Custom Store Project":     { icon: ShoppingBag,    color: "hsl(142, 64%, 38%)", bg: "hsla(142, 64%, 38%, 0.08)" },
  "Shopify Migrations":       { icon: ArrowRightLeft, color: "hsl(32, 90%, 50%)",  bg: "hsla(32, 90%, 50%, 0.08)" },
  "Shopify App Development":  { icon: Code,           color: "hsl(258, 60%, 55%)", bg: "hsla(258, 60%, 55%, 0.08)" },
  "Integrations":             { icon: Puzzle,         color: "hsl(200, 70%, 45%)", bg: "hsla(200, 70%, 45%, 0.08)" },
  "Ecommerce":                { icon: ShoppingBag,    color: "hsl(142, 64%, 38%)", bg: "hsla(142, 64%, 38%, 0.08)" },
  "Web Design":               { icon: Palette,        color: "hsl(340, 65%, 50%)", bg: "hsla(340, 65%, 50%, 0.08)" },
  "Web Development":          { icon: Globe,          color: "hsl(220, 70%, 50%)", bg: "hsla(220, 70%, 50%, 0.08)" },
  "AI & Automation":          { icon: Bot,            color: "hsl(258, 60%, 55%)", bg: "hsla(258, 60%, 55%, 0.08)" },
  "AI":                       { icon: Cpu,            color: "hsl(258, 60%, 55%)", bg: "hsla(258, 60%, 55%, 0.08)" },
  "Support & Maintenance":    { icon: Wrench,         color: "hsl(32, 90%, 50%)",  bg: "hsla(32, 90%, 50%, 0.08)" },
  "Digital Strategy":         { icon: Rocket,         color: "hsl(200, 70%, 45%)", bg: "hsla(200, 70%, 45%, 0.08)" },
};

interface HeaderWork {
  id: string;
  title: string;
  title_ar: string | null;
  slug: string;
  featured_image: string | null;
  desktop_screenshot: string | null;
  industry: string | null;
}

const DESKTOP_NAV_KEYS = ["Ecommerce", "Services", "Work", "Blog"];
const HAS_DROPDOWN_KEYS = ["Ecommerce", "Services"];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [hideNav, setHideNav] = useState(false);
  const [headerWorks, setHeaderWorks] = useState<HeaderWork[]>([]);
  const { items } = useMenuItems("header");
  const { items: burgerItems } = useMenuItems("burger");
  const { lang, localePath } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const t = (en: string, ar: string | null | undefined) => lang === "ar" && ar ? ar : en;

  // Derive burger menu structure from dynamic data
  const burgerPrimaryLinks = useMemo(() =>
    burgerItems.filter(i => i.group_key === "primary" && !i.parent_id).sort((a, b) => a.sort_order - b.sort_order),
    [burgerItems]
  );
  const burgerSections = useMemo(() => {
    const sections = burgerItems.filter(i => i.group_key === "section" && !i.parent_id).sort((a, b) => a.sort_order - b.sort_order);
    return sections.map(section => ({
      ...section,
      children: burgerItems.filter(i => i.parent_id === section.id).sort((a, b) => a.sort_order - b.sort_order),
    }));
  }, [burgerItems]);

  const handleLogoClick = (e: React.MouseEvent) => {
    const homePath = localePath("/");
    if (location.pathname === homePath || location.pathname === "/" || location.pathname === "/ar") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ctaEl = document.getElementById("cta-section");
    if (!ctaEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHideNav(entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(ctaEl);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    supabase
      .from("work")
      .select("id, title, title_ar, slug, featured_image, desktop_screenshot, industry")
      .eq("show_in_header", true)
      .eq("status", "published")
      .order("sort_order")
      .limit(6)
      .then(({ data }) => setHeaderWorks(data || []));
  }, []);

  const navItems = items.filter((i) => DESKTOP_NAV_KEYS.includes(i.label_en));
  const ctaItem = items.find((i) => i.label_en === "Start a Project");

  const getLabel = (item: MenuItem) => lang === "ar" && item.label_ar ? item.label_ar : item.label_en;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-150 ease-out ${
          hideNav ? "-translate-y-full opacity-0" : "translate-y-0 opacity-100"
        } ${
          scrolled
            ? "bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-sm"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="container mx-auto px-6">
          <div className={`flex items-center justify-between transition-all duration-200 ${scrolled ? "h-14 lg:h-16" : "h-16 lg:h-20"}`}>
            {/* Logo */}
            <Link to={localePath("/")} onClick={handleLogoClick} className="flex items-center">
              <img src={wavaiLogo} alt="Wavai" className="h-6 w-auto" />
            </Link>

            {/* Desktop Navigation — absolute center */}
            <nav className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
              {navItems.map((item) => (
                <div
                  key={item.id}
                  className="relative"
                  onMouseEnter={() => setActiveMenu(item.id)}
                  onMouseLeave={() => setActiveMenu(null)}
                >
                  <Link
                    to={localePath(item.url)}
                    className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {getLabel(item)}
                    {(item.children?.length || HAS_DROPDOWN_KEYS.includes(item.label_en)) && (
                      <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${activeMenu === item.id ? "rotate-180" : ""}`} />
                    )}
                  </Link>

                  {item.children && item.children.length > 0 && activeMenu === item.id && (
                    <div className="absolute top-full left-0 pt-2" style={{ animation: "fade-in 0.15s ease-out" }}>
                      {item.label_en === "Services" ? (
                        <div className="bg-card border border-border rounded-xl p-2 shadow-card w-[240px]">
                          <div className="flex flex-col">
                            {item.children.map((sub) => {
                              const brand = BRAND_MAP[sub.url];
                              return (
                                <Link
                                  key={sub.id}
                                  to={localePath(sub.url)}
                                  className="group/item flex items-center justify-between gap-3 px-3.5 py-3 rounded-lg transition-all"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = brand?.bg || "hsl(var(--secondary))";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                  }}
                                >
                                  <span className="text-sm font-medium text-foreground">{getLabel(sub)}</span>
                                  {brand && (
                                    <img
                                      src={brand.logo}
                                      alt=""
                                      className="h-5 max-w-[80px] object-contain opacity-0 group-hover/item:opacity-60 transition-opacity duration-200"
                                    />
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-card border border-border rounded-xl p-3 shadow-card min-w-[420px]">
                          <div className="grid grid-cols-2 gap-1.5">
                            {item.children.map((sub) => {
                              const style = DROPDOWN_STYLE[sub.label_en];
                              const IconComp = style?.icon;
                              return (
                                <Link
                                  key={sub.id}
                                  to={localePath(sub.url)}
                                  className="group/item flex items-start gap-3 px-3 py-3 rounded-lg transition-all hover:shadow-sm"
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = style?.bg || "hsl(var(--secondary))")}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                >
                                  {IconComp && (
                                    <div
                                      className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                                      style={{ backgroundColor: style.bg, color: style.color }}
                                    >
                                      <IconComp className="h-4 w-4" />
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-foreground">{getLabel(sub)}</div>
                                    {(lang === "ar" && sub.description_ar ? sub.description_ar : sub.description_en) && (
                                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                                        {lang === "ar" && sub.description_ar ? sub.description_ar : sub.description_en}
                                      </div>
                                    )}
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right side: CTA + Burger */}
            <motion.div className="flex items-center gap-2 sm:gap-3" layout transition={{ duration: 0.15, ease: "easeOut" }}>
              <motion.div layout transition={{ duration: 0.15, ease: "easeOut" }}>
                <LanguageSwitcher variant="compact" />
              </motion.div>
              <AnimatePresence>
                {ctaItem && scrolled && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, filter: "blur(4px)" }}
                    animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                    exit={{ opacity: 0, scale: 0.9, filter: "blur(4px)" }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="hidden sm:block"
                  >
                    <Button variant="hero" size="sm" asChild>
                      <Link to={localePath(ctaItem.url)}>{getLabel(ctaItem)}</Link>
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
              <button
                className="p-2 text-foreground"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
              >
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Full-Screen Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[55] bg-primary overflow-y-auto origin-top"
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            exit={{ scaleY: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Overlay header */}
            <motion.div
              className="container mx-auto px-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15, delay: 0.15 }}
            >
              <div className="flex items-center justify-between h-16 lg:h-20">
                <Link to={localePath("/")} onClick={(e) => { setMobileOpen(false); handleLogoClick(e); }} className="flex items-center">
                  <img src={wavaiLogo} alt="Wavai" className="h-6 w-auto brightness-0 invert" />
                </Link>
                <button className="p-2 text-white" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </motion.div>

            {/* Content */}
            <div className="container mx-auto px-6 pt-4 pb-4 lg:pt-20 lg:pb-20 h-[calc(100vh-4rem)] lg:h-auto flex flex-col">
              {/* 1. Primary nav links — from DB */}
              <nav className="flex flex-col gap-0.5 lg:gap-1 mb-6 lg:mb-10">
                {burgerPrimaryLinks.map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: 0.18 + i * 0.04, ease: "easeOut" }}
                  >
                    <Link
                      to={localePath(item.url)}
                      className="text-4xl lg:text-6xl font-bold text-white/90 hover:text-white hover:translate-x-2 transition-all duration-200 w-fit block leading-[1.05]"
                      onClick={() => setMobileOpen(false)}
                      target={item.target === "_blank" ? "_blank" : undefined}
                      rel={item.is_external ? "noopener noreferrer" : undefined}
                    >
                      {t(item.label_en, item.label_ar)}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* 2. Sections (Services, Solutions, etc.) + Featured Projects */}
              <motion.div
                className="flex gap-10 lg:gap-20 mb-auto lg:mb-12"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.32, ease: "easeOut" }}
              >
                {burgerSections.map((section) => (
                  <div key={section.id}>
                    <h3 className="text-xs lg:text-base font-bold text-white/60 mb-2 lg:mb-5 uppercase tracking-wider">
                      {t(section.label_en, section.label_ar)}
                    </h3>
                    <ul className="space-y-1 lg:space-y-3.5">
                      {section.children.map((s) => (
                        <li key={s.id}>
                          <Link
                            to={localePath(s.url)}
                            className="text-sm lg:text-base text-white/80 hover:text-white transition-colors"
                            onClick={() => setMobileOpen(false)}
                            target={s.target === "_blank" ? "_blank" : undefined}
                            rel={s.is_external ? "noopener noreferrer" : undefined}
                          >
                            {t(s.label_en, s.label_ar)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {/* Featured Projects — vertical list on the right (desktop) */}
                {headerWorks.length > 0 && (
                  <div className="hidden lg:block ml-auto">
                    <h3 className="text-base font-bold text-white/60 mb-5 uppercase tracking-wider">
                      {lang === "ar" ? "مشاريع مختارة" : "Featured Projects"}
                    </h3>
                    <div className="flex flex-col gap-4">
                      {headerWorks.map((w) => (
                        <Link
                          key={w.id}
                          to={localePath(`/work/${w.slug}`)}
                          onClick={() => setMobileOpen(false)}
                          className="group flex items-center gap-4"
                        >
                          <div className="rounded-lg overflow-hidden border border-white/10 w-24 aspect-[16/10] bg-white/5 shrink-0">
                            <img
                              src={resolveAssetUrl(w.desktop_screenshot || w.featured_image)}
                              alt={t(w.title, w.title_ar)}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          </div>
                          <p className="text-sm text-white/70 group-hover:text-white transition-colors">
                            {t(w.title, w.title_ar)}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Featured Projects — mobile only (horizontal scroll) */}
              {headerWorks.length > 0 && (
                <motion.div
                  className="lg:hidden mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: 0.38 }}
                >
                  <h3 className="text-xs font-bold text-white/60 mb-3 uppercase tracking-wider">
                    {lang === "ar" ? "مشاريع مختارة" : "Featured Projects"}
                  </h3>
                  <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                    {headerWorks.map((w) => (
                      <Link
                        key={w.id}
                        to={localePath(`/work/${w.slug}`)}
                        onClick={() => setMobileOpen(false)}
                        className="group shrink-0 w-40"
                      >
                        <div className="rounded-xl overflow-hidden border border-white/10 aspect-[16/10] bg-white/5">
                          <img
                            src={resolveAssetUrl(w.desktop_screenshot || w.featured_image)}
                            alt={t(w.title, w.title_ar)}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <p className="text-xs text-white/70 group-hover:text-white mt-2 truncate transition-colors">
                          {t(w.title, w.title_ar)}
                        </p>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* 4. Contact details */}
              <motion.div
                className="flex gap-4 mb-2 lg:mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: 0.4 }}
              >
                <a href="tel:+96597558522" dir="ltr" className="text-sm lg:text-base text-white/80 hover:text-white underline underline-offset-2 transition-colors">+965 97558522</a>
                <a href="mailto:hello@wavai.com" className="text-sm lg:text-base text-white/80 hover:text-white underline underline-offset-2 transition-colors">hello@wavai.com</a>
              </motion.div>

              {/* 5. Address */}
              <motion.p
                className="text-sm lg:text-base text-white/50 leading-snug mb-3 lg:mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: 0.42 }}
              >
                {lang === "ar" ? (
                  <>مبنى ACE هاردوير، الطابق الثاني، شارع تونس، حولي، الكويت</>
                ) : (
                  <>ACE Hardware Building, 2nd Floor,<br className="hidden lg:block" /> Tunis Street, Hawally, Kuwait.</>
                )}
              </motion.p>

              {/* 6. Social icons */}
              <motion.div
                className="flex items-center gap-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: 0.44 }}
              >
                <a href="https://www.facebook.com/wavaicom" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-white/60 hover:text-white transition-colors"><Facebook className="w-5 h-5" /></a>
                <a href="https://www.instagram.com/wavaicom" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-white/60 hover:text-white transition-colors"><Instagram className="w-5 h-5" /></a>
                <a href="https://x.com/wavaicom" target="_blank" rel="noopener noreferrer" aria-label="X" className="text-white/60 hover:text-white transition-colors">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://www.linkedin.com/company/wavai" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-white/60 hover:text-white transition-colors"><Linkedin className="w-4 h-4" /></a>
                <a href="https://www.tiktok.com/@wavaidotae" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-white/60 hover:text-white transition-colors">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.51a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.71a8.21 8.21 0 0 0 4.76 1.52V6.69h-1z"/></svg>
                </a>
                <a href="https://wa.me/+96597558522" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-white/60 hover:text-white transition-colors">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
